import { GraphChildProps, TimeRange, useGraphCtx } from '@mi-front/core';
import { Animate } from '@ui/components';
import { rgba } from 'polished';
import React, { memo, useState } from 'react';
import { DraggableMarker } from '../../../components/stream/marker/DraggableMarker';
import { DurationLabel, StartEndLabel } from '../label';
import { StreamEl } from '../stream-el/StreamEl';

interface PropsP extends GraphChildProps {
  readonly tsA: number | null;
  readonly tsB: number | null;
  readonly menu: React.ReactNode | null;
  readonly onChangeA: (ts: number, isDragging: boolean) => void;
  readonly onChangeB: (ts: number, isDragging: boolean) => void;
  readonly children?: (range: TimeRange<number>) => React.ReactNode;
  readonly color: string;
  readonly tz?: string;
  readonly menuPosition?: 'start' | 'end';
}

function TimeSelectionP(p: PropsP) {
  const { tsA, tsB, scale, parentHeight, menu, onChangeA, onChangeB, color, children, tz } = p;

  const selectionExists = tsA && tsB;
  const minTs = Math.min(tsA || Infinity, tsB || Infinity);
  const maxTs = Math.max(tsA || 0, tsB || 0);

  const range = {
    from: minTs,
    to: maxTs,
  };

  const x1 = tsA !== null ? scale(tsA)! : null;
  const x2 = tsB !== null ? scale(tsB)! : null;

  return (
    <React.Fragment>
      {selectionExists && (
        <span
          data-selection-from-ts={minTs}
          data-selection-to-ts={maxTs}
          style={{ display: 'none' }}
        />
      )}
      {selectionExists && x1 !== null && x2 !== null && (
        <div
          className="time-selection__background"
          style={{
            position: 'absolute',
            zIndex: -1000,
            left: `${Math.min(x1, x2)}px`,
            width: `${Math.abs(x2 - x1)}px`,
            height: `${parentHeight}px`,
            background: rgba(color, 0.15),
          }}
        />
      )}
      {tsA && (
        <React.Fragment>
          <DraggableMarker ts={tsA} onChange={onChangeA} color={color} />
          <StreamEl
            className="time-selection__start-label-el"
            position="bottom"
            ts={minTs}
            zIndex={20}
          >
            <StartEndLabel ts={minTs} isStart tz={tz} />
          </StreamEl>
        </React.Fragment>
      )}
      {tsB && (
        <React.Fragment>
          <DraggableMarker ts={tsB} onChange={onChangeB} color={color} />
          <StreamEl className="time-selection__end-label-el" ts={maxTs} zIndex={20} anchor="right">
            <StartEndLabel ts={maxTs} isEnd tz={tz} />
          </StreamEl>
        </React.Fragment>
      )}
      {selectionExists && (
        <React.Fragment>
          <StreamEl
            className="time-selection__menu-el"
            ts={p.menuPosition === 'end' ? maxTs : minTs}
            zIndex={2}
            position="top"
            offsetY={36}
            offsetX={-32}
          >
            {menu}
          </StreamEl>
          <StreamEl
            className="time-selection__start-label-el"
            position="bottom"
            ts={minTs}
            zIndex={2}
            offsetY={-30}
          >
            <DurationLabel range={range} />
          </StreamEl>
        </React.Fragment>
      )}
      {children && children(range)}
    </React.Fragment>
  );
}

interface Props {
  readonly tsA: number | null;
  readonly tsB: number | null;
  readonly menu?: React.ReactNode;
  readonly onChange: (range: { tsA: number | null; tsB: number | null }) => void;
  readonly color: string;
  readonly tz?: string;
  readonly menuPosition?: 'start' | 'end';
}

export const TimeSelection = memo<Props>(function TimeSelection(p) {
  const { scale, height, width } = useGraphCtx();
  const [isChanging, setChanging] = useState(false);
  const tsA = p.tsA;
  const tsB = p.tsB;

  if (!tsA && !tsB) {
    return <div className="time-selection--empty" />;
  }

  const showMenu = Boolean(tsA && tsB && !isChanging && p.menu);

  return (
    <TimeSelectionP
      tz={p.tz}
      tsA={tsA}
      tsB={tsB}
      parentHeight={height}
      parentWidth={width}
      scale={scale}
      color={p.color}
      menu={<Animate show={showMenu}>{p.menu}</Animate>}
      menuPosition={p.menuPosition}
      onChangeA={(value, isDragging) => {
        setChanging(isDragging);
        p.onChange({
          tsA: value,
          tsB: tsB!,
        });
      }}
      onChangeB={(value, isDragging) => {
        setChanging(isDragging);
        p.onChange({
          tsA: tsA!,
          tsB: value,
        });
      }}
    />
  );
});
